import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listEvents, ackEventsAll } from '../api/eventsApi';
import { useAuthStore } from '../stores/authStore';
import { subscribe } from '../hooks/useWebSocket';
import type { EventListItem } from '../api/types';

// 권위: docs/ARCH_DASHBOARD.md §4.0.2
// 벨 아이콘 + FATAL/ERROR 미확인 뱃지 + 드롭다운 10건.
export function NotificationCenter(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [realtimeItems, setRealtimeItems] = useState<EventListItem[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const user = useAuthStore((s) => s.user);

  const { data } = useQuery({
    queryKey: ['events', 'unacked-critical'],
    queryFn: () =>
      listEvents({
        severity: 'FATAL,ERROR',
        unacked: true,
        pageSize: 10,
      }),
    // 30초 폴링 fallback (§4.0.2 "업데이트 주기: WebSocket 실시간 / 폴링 fallback 30초")
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  // 실시간 구독 — FATAL/ERROR 만 클라이언트 필터
  useEffect(() => {
    const off = subscribe('/topic/system-events', (msg) => {
      if (!msg.body) return;
      try {
        const parsed = JSON.parse(msg.body) as Partial<EventListItem> & Record<string, unknown>;
        const sev = (parsed.severity ?? '') as string;
        if (sev !== 'FATAL' && sev !== 'ERROR') return;
        setRealtimeItems((prev) => {
          const next = [
            {
              eventId: (parsed.eventId as string) ?? (parsed.event_id as string) ?? crypto.randomUUID(),
              occurredAt: (parsed.occurredAt as string) ?? (parsed.occurred_at as string) ?? new Date().toISOString(),
              severity: sev as EventListItem['severity'],
              sourceAgent: (parsed.sourceAgent as string) ?? (parsed.source_agent as string) ?? '',
              eventCode: (parsed.eventCode as string) ?? (parsed.event_code as string) ?? '',
              summary: (parsed.summary as string) ?? '',
              entityRefs: (parsed.entityRefs as Record<string, unknown>) ?? (parsed.entity_refs as Record<string, unknown>) ?? null,
              traceId: (parsed.traceId as string) ?? (parsed.trace_id as string) ?? null,
              acked: false,
            } as EventListItem,
            ...prev,
          ];
          return next.slice(0, 10);
        });
      } catch {
        /* ignore */
      }
    });
    return off;
  }, []);

  // 바깥 클릭 시 닫기
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const serverItems = data?.items ?? [];
  const seen = new Set<string>();
  const merged: EventListItem[] = [];
  for (const it of [...realtimeItems, ...serverItems]) {
    if (seen.has(it.eventId)) continue;
    seen.add(it.eventId);
    merged.push(it);
    if (merged.length >= 10) break;
  }

  const unackedCount = (data?.unackedCount ?? 0) + realtimeItems.filter((i) => !i.acked).length;

  async function onAckAll(): Promise<void> {
    if (unackedCount === 0) return;
    try {
      // 드롭다운 10건이 아니라 FATAL/ERROR 전체 미확인을 한 번의 서버 쿼리로 ack.
      await ackEventsAll('FATAL,ERROR', user?.email ?? 'unknown');
      setRealtimeItems([]);
      queryClient.invalidateQueries({ queryKey: ['events'] });
    } catch {
      /* 에러는 토스트로 노출 예정 (후속) */
    }
  }

  return (
    <div className="notification-center" ref={containerRef}>
      <button
        type="button"
        className="notification-center__trigger"
        aria-label={`알림 ${unackedCount}건 미확인`}
        onClick={() => setOpen((v) => !v)}
      >
        <Bell size={18} aria-hidden />
        {unackedCount > 0 && <span className="notification-center__badge">{unackedCount}</span>}
      </button>
      {open && (
        <div className="notification-center__dropdown" role="dialog" aria-label="알림 센터">
          <div className="notification-center__header">알림 센터 ({unackedCount}개 미확인)</div>
          <ul className="notification-center__list">
            {merged.length === 0 && <li className="notification-center__empty">미확인 알림이 없습니다.</li>}
            {merged.map((it) => (
              <li key={it.eventId} className={`notification-center__item notification-center__item--${it.severity.toLowerCase()}`}>
                <button
                  type="button"
                  className="notification-center__item-link"
                  onClick={() => {
                    setOpen(false);
                    navigate(`/logs/${it.eventId}`);
                  }}
                >
                  <div className="notification-center__item-code">
                    <span className="notification-center__severity">{it.severity}</span>
                    <span>{it.eventCode}</span>
                  </div>
                  <div className="notification-center__item-summary">{it.summary}</div>
                  <div className="notification-center__item-meta">
                    {it.sourceAgent} · {new Date(it.occurredAt).toLocaleString('ko-KR')}
                  </div>
                </button>
              </li>
            ))}
          </ul>
          <div className="notification-center__footer">
            <button type="button" onClick={onAckAll} disabled={unackedCount === 0}>
              모두 읽음 처리
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/logs');
              }}
            >
              전체 보기 →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
